import { useMemo } from 'react'
import { RBACAction, RBACModule, systemRoleHasPermission } from '@numeo/types-core'
import { useAppSelector } from 'hooks'
import { usePermissions } from 'hooks/usePermissions'
import { useManagedTeamGroups } from 'hooks/useManagedTeamGroups'

export interface BoxTruckAccess {
    /** Loadboard / Bidding / Map — the working surfaces every box-truck dispatcher uses. */
    surfaces: boolean
    /** Driver Verifications / My Drivers / Company Owners — the operator consoles. */
    management: boolean
    /**
     * Admin/owner, with no RBAC checkbox escape hatch.
     *
     * Stricter than `management` on purpose: that tier is handed to recruiters and senior dispatchers so they
     * can work the driver consoles, and the surfaces on this one are not theirs to reach.
     */
    admin: boolean
    /**
     * Dispatcher Analytics — admin/owner, plus a Dispatch Manager.
     *
     * The manager is the one exception to "the people being measured do not read the ranking", and only
     * because the server narrows what they receive to the dispatchers on the desks they manage: measuring
     * your own team is the job. Everyone else — including the `BOX_TRUCK_DRIVERS` checkbox holders — stays
     * out. Mirrored server-side by `boxTruckAnalyticsGuard`, and the mirror has to be exact: a client that
     * is stricter than the guard hides a page the server would have served, which is the same broken
     * promotion as a guard that refuses.
     */
    analytics: boolean
}

/**
 * Who can open which half of the Box Truck section (on top of the operator allowlist, which gates the
 * whole section). One resolver shared by the sidebar rows and the route guards so they can't drift.
 *
 * - Admins and the application owner always see everything.
 * - Everyone else goes through `hasPermission` — the Role builder's "Box Truck Loadboard" /
 *   "Box Truck Driver Management" VIEW checkboxes (custom roles and editable system-role templates
 *   both flow through it). By default that yields the role split: dispatchers get the working
 *   surfaces, management is denied (`SYSTEM_ROLE_RESTRICTIONS`).
 * - Surfaces keep a hardcoded-restrictions fallback for SYSTEM-role users: an application whose
 *   system-role templates were seeded before the box-truck modules existed resolves them to false,
 *   which would silently kick every dispatcher off the loadboard. The fallback allows what the
 *   hardcoded matrix allows; management needs no fallback because its hardcoded default is deny,
 *   so a stale template can't create a false positive there.
 */
export const useBoxTruckAccess = (): BoxTruckAccess => {
    const user = useAppSelector((state) => state.auth.user)
    const { hasPermission, isAdmin, isOwner } = usePermissions()
    const hasCustomRole = Boolean(user?.rbacRoleId)
    const userRole = user?.role
    const isAdminUser = isAdmin || isOwner
    // Only a non-admin can gain anything from managing a desk, so admins never pay the request.
    const { groups: managedGroups } = useManagedTeamGroups(!isAdminUser)
    const managesGroups = managedGroups.length > 0
    // Memoized (hasPermission is itself memoized on application+user) so consumers can use the object
    // as a hook-dependency without re-filtering the sidebar every render.
    // Whether the company-aware DEFAULT applies to this member. It is false the moment a stored template or
    // a custom role has an opinion, so it adds the two Box Truck modules only where nothing else answered —
    // and the static permission table cannot answer this one, because it does not know which companies run
    // Box Truck. Deliberately NOT `profilesFromRole`: that flag carries the DRIVER grant, so using it for
    // `surfaces` would let a Drivers grant an owner wrote open the Loadboard they left out.
    const accountantDefault = useAppSelector((state) => state.boxTruckLoads.accountantDefault)
    return useMemo(() => {
        const management = isAdminUser || accountantDefault || hasPermission(RBACModule.BOX_TRUCK_DRIVERS, RBACAction.VIEW)
        return {
            surfaces:
                isAdminUser ||
                accountantDefault ||
                hasPermission(RBACModule.BOX_TRUCK_LOADS, RBACAction.VIEW) ||
                (!hasCustomRole && userRole !== undefined && systemRoleHasPermission(userRole, RBACModule.BOX_TRUCK_LOADS, RBACAction.VIEW)),
            management,
            admin: isAdminUser,
            // Running a desk IS the Dispatch Manager role, and this page is what the role is for — so the
            // desk alone opens it, exactly as `boxTruckAnalyticsGuard` now decides. Requiring the
            // BOX_TRUCK_DRIVERS grant on top left a promoted manager redirected off their own page: the
            // system Dispatcher template does not carry that grant, so the promotion never worked alone.
            analytics: isAdminUser || managesGroups,
        }
    }, [isAdminUser, accountantDefault, hasCustomRole, userRole, hasPermission, managesGroups])
}
